import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../App';
import { get } from '../data/api';
import {
  JAPAN_LIFE_CATEGORIES,
  JAPAN_LIFE_GUIDES,
  findServiceBySlug,
} from '../data/japan_life';
import { DEFAULT_PREFECTURE } from '../data/japan_locations';
import { ServiceProviderModal } from './CommunitySubmit';
import './Community.css';
import './JapanLife.css';

function JobListMini({ type, prefecture }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    get('jobs', { action: 'list', type, prefecture, limit: 5 })
      .then(d => setJobs(d.jobs || []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, [type, prefecture]);

  if (loading) return <p className="text-sm text-muted">Loading jobs…</p>;
  if (!jobs.length) return (
    <p className="text-sm text-muted">
      No {type === 'baito' ? 'baito' : 'full-time'} listings in {prefecture} yet.{' '}
      <Link to="/community?tab=jobs">Post one</Link>
    </p>
  );

  return (
    <div>
      {jobs.slice(0, 5).map(j => (
        <div key={j.id} className="jl-provider-card">
          <div className="jl-provider-name">{j.title}</div>
          <div className="jl-provider-meta">{j.company} · {j.salary || 'Salary TBD'}</div>
          <p className="text-sm">{j.description?.slice(0, 90)}…</p>
          {(j.apply_url || j.apply_email) && (
            <a href={j.apply_url || `mailto:${j.apply_email}`} className="btn btn-outline btn-sm" style={{ marginTop: 8 }}>
              Apply
            </a>
          )}
        </div>
      ))}
      <Link to={`/community?tab=jobs&prefecture=${prefecture}`} className="btn btn-ghost btn-sm" style={{ marginTop: 8 }}>
        View all jobs →
      </Link>
    </div>
  );
}

export default function JapanLife() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { token } = useApp();

  const [activeCat, setActiveCat] = useState(JAPAN_LIFE_CATEGORIES[0].id);
  const [serviceSlug, setServiceSlug] = useState(params.get('service') || 'housing');
  const [prefecture, setPrefecture] = useState(params.get('prefecture') || DEFAULT_PREFECTURE);
  const [providers, setProviders] = useState([]);
  const [showOffer, setShowOffer] = useState(false);

  const category = JAPAN_LIFE_CATEGORIES.find(c => c.id === activeCat) || JAPAN_LIFE_CATEGORIES[0];
  const service  = findServiceBySlug(serviceSlug) || category.services[0];
  const guide    = JAPAN_LIFE_GUIDES[serviceSlug];

  useEffect(() => {
    setParams({ service: serviceSlug, prefecture }, { replace: true });
  }, [serviceSlug, prefecture, setParams]);

  useEffect(() => {
    if (service?.api === 'jobs') return;
    get('service_providers', { action: 'list', service_slug: serviceSlug, prefecture })
      .then(d => setProviders(d.providers || []))
      .catch(() => setProviders([]));
  }, [serviceSlug, prefecture, service]);

  useEffect(() => {
    const cat = JAPAN_LIFE_CATEGORIES.find(c => c.services.some(s => s.slug === serviceSlug));
    if (cat) setActiveCat(cat.id);
  }, [serviceSlug]);

  return (
    <div className="jl-page">
      <nav className="comm-nav">
        <div className="container comm-nav-inner">
          <Link to="/" className="comm-logo">mudiaspora<span style={{ color: 'var(--text-muted)' }}>.</span></Link>
          <div className="comm-nav-links">
            <Link to="/community">Japan Community</Link>
            <Link to="/japan-life">Japan Life</Link>
            <Link to="/kenya-connect">Kenya Connect</Link>
            <Link to="/">Kenya Services</Link>
            {token ? <Link to="/dashboard">Dashboard</Link> : <Link to="/login">Sign In</Link>}
          </div>
        </div>
      </nav>

      <section className="jl-hero">
        <div className="container">
          <h1>Life in <span>Japan</span></h1>
          <p>Guides, community providers, and job listings — from Kenyans who've been through it.</p>
        </div>
      </section>

      <div className="container jl-layout">
        <aside className="jl-sidebar">
          {JAPAN_LIFE_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              type="button"
              className={`jl-cat-btn ${activeCat === cat.id ? 'active' : ''}`}
              onClick={() => { setActiveCat(cat.id); setServiceSlug(cat.services[0].slug); }}
            >
              <span>{cat.icon}</span> {cat.name}
            </button>
          ))}
          <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid var(--border)' }} />
          <div className="comm-field" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Prefecture</label>
            <select value={prefecture} onChange={e => setPrefecture(e.target.value)} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid var(--border)' }}>
              {['Tokyo', 'Osaka', 'Aichi', 'Fukuoka', 'Hokkaido', 'Kanagawa', 'Kyoto', 'Hyogo', 'Saitama', 'Chiba'].map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        </aside>

        <main>
          <div className="jl-service-grid">
            {category.services.map(svc => (
              <button
                key={svc.slug}
                type="button"
                className={`jl-service-chip ${serviceSlug === svc.slug ? 'active' : ''}`}
                onClick={() => setServiceSlug(svc.slug)}
              >
                <div className="jl-service-chip-icon">{svc.icon}</div>
                <div className="jl-service-chip-name">{svc.name}</div>
              </button>
            ))}
          </div>

          {guide && (
            <div className="jl-guide">
              <h2>{guide.title}</h2>
              <ol>{guide.steps.map((step, i) => <li key={i}>{step}</li>)}</ol>
              {guide.tips?.length > 0 && (
                <div className="jl-tips"><strong>Community tips:</strong> {guide.tips.join(' ')}</div>
              )}
            </div>
          )}

          {service?.api === 'jobs' ? (
            <>
              <h3 className="jl-section-title">{service.name} in {prefecture}</h3>
              <JobListMini type={service.type} prefecture={prefecture} />
            </>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <h3 className="jl-section-title" style={{ margin: 0 }}>Community providers</h3>
                {token ? (
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowOffer(true)}>
                    + Offer this service
                  </button>
                ) : (
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => navigate('/login')}>
                    Sign in to list your service
                  </button>
                )}
              </div>
              {providers.length === 0 ? (
                <p className="text-sm text-muted">No providers listed yet for {service.name} in {prefecture}.</p>
              ) : (
                providers.map(p => (
                  <div key={p.id} className="jl-provider-card">
                    <div className="jl-provider-name">{p.headline}</div>
                    <div className="jl-provider-meta">
                      {p.provider_name} · {[p.japan_city, p.japan_prefecture].filter(Boolean).join(', ')}
                      {p.rate && ` · ${p.rate}`}
                    </div>
                    {p.detail && <p className="text-sm">{p.detail}</p>}
                    {p.whatsapp && (
                      <a href={`https://wa.me/${p.whatsapp.replace(/\D/g, '')}`} className="btn btn-outline btn-sm" style={{ marginTop: 8 }}>
                        Contact
                      </a>
                    )}
                  </div>
                ))
              )}
            </>
          )}

          <div style={{ marginTop: 32, padding: 20, background: 'var(--green-pale)', borderRadius: 'var(--radius-lg)' }}>
            <strong>Need Kenya-side help too?</strong>
            <p className="text-sm" style={{ marginTop: 6, color: 'var(--text-2)' }}>
              Manage farms, rent, and errands back home from the{' '}
              <Link to="/dashboard">Kenya dashboard</Link>.
            </p>
          </div>
        </main>
      </div>

      {showOffer && (
        <ServiceProviderModal
          serviceSlug={serviceSlug}
          serviceName={service.name}
          onClose={() => setShowOffer(false)}
          onDone={() => {
            get('service_providers', { action: 'list', service_slug: serviceSlug, prefecture })
              .then(d => setProviders(d.providers || []));
          }}
        />
      )}
    </div>
  );
}
